import { Injectable, inject } from '@angular/core';
import { QueryClient } from '@tanstack/angular-query-experimental';

import { recentObjectsQueriesFilter } from 'src/app/api/queries/user-api.queries';
import { augmentObjectWithIconAndImage } from 'src/app/shared/utils/object-utils';

import { DataObject } from '../models/core.models';

/**
 * Cache surgery on the recent-objects list. No fetch-if-missing tier: both consumers
 * observe `injectRecentObjectsQuery` reactively, so nothing needs a one-shot read.
 *
 * Both writes are patches rather than invalidations, and only one of them is
 * server-backed. See `injectRecentObjectsQuery` for why the query never refetches on
 * its own.
 */
@Injectable({ providedIn: 'root' })
export class RecentObjectsService {
  private readonly queryClient = inject(QueryClient);

  /**
   * `objectChanged` - the optimistic prepend: the visited object goes to the front
   * and its previous row is dropped. `CoreEffects.updateRecentObject$` persists it,
   * and a failed POST is not rolled back.
   *
   * Dedupe is on `object_id`, because the server's rows carry both `id` and
   * `object_id` set to the same object id, and so do the objects the feed /
   * dashboard / workspace routes hand to `objectChanged`.
   *
   * No cap: the server returns at most 10 rows, but a session's prepends are
   * unbounded until the next fetch.
   */
  prepend(object: DataObject): void {
    this.patchEvery((recentObjects) => [
      augmentObjectWithIconAndImage(object) as DataObject,
      ...recentObjects.filter((o) => o.object_id !== object.id),
    ]);
  }

  /**
   * `removeSearchSuggestion` - the quick-search "remove" button. **Client-only:
   * there is no remove endpoint**, so this drops the row from the cache and nothing
   * else; it comes back on the next explicit refresh (company switch or a new
   * session).
   *
   * Matches on `id`, not `object_id`, and removes the first match only.
   */
  remove(suggestionId: string): void {
    this.patchEvery((recentObjects) => {
      const index = recentObjects.findIndex((item) => item.id === suggestionId);
      if (index === -1) {
        return recentObjects;
      }

      return [
        ...recentObjects.slice(0, index),
        ...recentObjects.slice(index + 1),
      ];
    });
  }

  /** Drop the cached recent-objects list, forcing a refetch. */
  invalidate(): void {
    void this.queryClient.invalidateQueries(recentObjectsQueriesFilter());
  }

  private patchEvery(
    patch: (recentObjects: DataObject[]) => DataObject[],
  ): void {
    this.queryClient.setQueriesData<DataObject[]>(
      recentObjectsQueriesFilter(),
      (recentObjects) => (recentObjects ? patch(recentObjects) : recentObjects),
    );
  }
}
